import type { ReactNode } from "react";
import { Lock } from "lucide-react";
import { Button, Card, Chip } from "@/components/ds";

/** The chip on a section only Athlyn admins can change (OP-D7). */
export const ADMIN_ONLY_CHIP = "Athlyn admins only";
/** What an Athlyn team member reads on such a section (E7). */
export const ADMIN_ONLY_NOTE = "Only Athlyn admins can change this";

export type SettingsSectionKey = "details" | "language" | "app" | "branding" | "features";

interface SectionFrameProps {
  section: SettingsSectionKey;
  title: string;
  hint?: ReactNode;
  /** Shows the "Athlyn admins only" chip next to the title. */
  adminOnly?: boolean;
  children: ReactNode;
  /** The save bar; omitted for a read-only view. */
  footer?: ReactNode;
}

/** One Settings section: title, hint, body and its own save bar (`org-settings-<section>`). */
export function SectionFrame({ section, title, hint, adminOnly, children, footer }: SectionFrameProps) {
  return (
    <Card className="px-5 py-4" data-testid={`org-settings-${section}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[15px] font-semibold text-ds-ink">{title}</h2>
        {adminOnly && <Chip tone="neutral">{ADMIN_ONLY_CHIP}</Chip>}
      </div>
      {hint && <p className="mt-0.5 text-[13px] text-ds-ink2">{hint}</p>}
      <div className="mt-4 space-y-4">{children}</div>
      {footer && <div className="mt-5 border-t border-ds-line pt-3.5">{footer}</div>}
    </Card>
  );
}

interface SaveBarProps {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  note?: ReactNode;
  /** Label of the save button (default "Save"). */
  saveLabel?: string;
}

/** Cancel + Save; Save stays disabled until something changes. */
export function SaveBar({ dirty, saving, onSave, onCancel, note, saveLabel = "Save" }: SaveBarProps) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {note && <span className="mr-auto text-[12.5px] text-ds-ink3">{note}</span>}
      <Button variant="secondary" disabled={!dirty || saving} onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" disabled={!dirty} loading={saving} onClick={onSave}>
        {saveLabel}
      </Button>
    </div>
  );
}

/** The read-only note team members see on an admin-only setting (E7). */
export function AdminOnlyNote() {
  return (
    <p className="flex items-center gap-1.5 text-[12.5px] text-ds-ink3" data-testid="admin-only-note">
      <Lock className="h-3.5 w-3.5" aria-hidden="true" />
      {ADMIN_ONLY_NOTE}
    </p>
  );
}

interface ReadOnlyRowProps {
  label: string;
  value: ReactNode;
}

/** A label / value pair for a read-only view. */
export function ReadOnlyRow({ label, value }: ReadOnlyRowProps) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[180px_1fr] sm:gap-3">
      <dt className="text-[13px] text-ds-ink3">{label}</dt>
      <dd className="text-[13.5px] text-ds-ink">{value}</dd>
    </div>
  );
}
